// @ts-strict-ignore
import { createReducer, on } from '@ngrx/store';

import * as AppErrorActions from 'src/app/core/app-error/store/app-error.actions';
import * as MotorizationActions from 'src/app/features/motorization/store/motorization.actions';
import * as DealerLocatorActions from 'src/app/shared/dealer-locator/store/dealer-locator.actions';
import { secureRandom } from 'src/app/utils/functions/secure-random';
import { ExecutionMode } from 'src/app/utils/models/execution-mode.enum';
import type { OnlineCodeData } from 'src/app/utils/models/onlinecode-data.interface';

import type { MbccState } from '../mbcc/mbcc.state';
import { RuntimeVariables } from '../runtime-variables/runtime-variables.enum';

import * as ConfigurationActions from './../../features/configuration/store/configuration.actions';
import * as CommonActions from './../common/common.actions';
import * as FinancingActions from './../financing/financing.actions';
import * as RuntimeVariableActions from './../runtime-variables/runtime-variables.actions';
import * as UserConfigActions from './../user-config/user-config.actions';

export const initialMbccState: MbccState = {
  currentMediaQuery: undefined,
  currentVehicleId: undefined,
  isInitialMotorization: undefined,
  currentSingleChoiceFilter: undefined,
  currentMotorization: undefined,
  hasAbTestingUserConsent: false,
  isConsentSystemInitializationDone: false,
  headerInfoHeight: undefined,
  stage: undefined,
  stageViewConfiguration: undefined,
  isLoading: false,
  filterResult: undefined,
  isRowLayoutUsed: false,
  isModalOpen: false,
  authState: undefined,
  vehicleImageUrl: undefined,
  backendDataVersion: undefined,
  isInAemPreviewMode: undefined, // will be determined
  executionContext: ExecutionMode.Standalone,
  onewebLanguage: undefined,
  onewebCountry: undefined,
  isPlsInitialized: undefined,
  isB2bPriceOnSummaryPageVisible: false,
  lastIgnoredVideoIdWhenAllVideosStopped: undefined,
  onlineCodeData: undefined,
  isFcisInitialized: false,
  isFcisWidgetOpen: false,
  isFcisRateConfirmed: false,
  calculationResult: undefined,
  isFcisCalculationAvailable: false,
  fcisVersion: '',
  postalCode: undefined,
  carlConfig: undefined,
  carlTranslations: undefined,
  vehicleSummaryResult: undefined,
  vehicles: undefined,
  filterData: undefined,
  outlets: undefined,
  currentError: {
    statusCode: undefined,
    message: undefined,
    name: undefined,
    type: undefined
  },
  vehicleId: undefined,
  isSimilarStockCarsAvailable: false,
  interior360DataUrl: undefined,
  interiorParameter: undefined,
  stageView: undefined,
  screenMirroringCredentials: undefined,
  feasibilityRequestPending: false,
  vehicleLengths: undefined,
  preConfigurations: undefined,
  isSummarySidebarShown: false,
  displayShadowUnderStage: false,
  isNextStepButtonVisible: true,
  isInitiallyBaumusterEntry: false
};

export const mbccReducer = createReducer(
  initialMbccState,
  on(
    CommonActions.updateMediaQuery,
    (state, action): MbccState => ({
      ...state,
      currentMediaQuery: action.payload
    })
  ),
  on(
    CommonActions.updateAbTestingUserConsent,
    (state, action): MbccState => ({
      ...state,
      hasAbTestingUserConsent: action.hasAbTestingUserConsent
    })
  ),
  on(
    CommonActions.updateConsentSystemInitializationDone,
    (state): MbccState => ({
      ...state,
      isConsentSystemInitializationDone: true
    })
  ),
  on(
    CommonActions.updateCurrentMotorization,
    (state, action): MbccState => ({
      ...state,
      currentMotorization: action.vehicle
    })
  ),
  on(
    CommonActions.updateCurrentSingleChoiceFilter,
    (state, action): MbccState => ({
      ...state,
      currentSingleChoiceFilter: action.filter
    })
  ),
  on(
    CommonActions.finishSetNavigationGroups,
    (state, action): MbccState => ({
      ...state,
      navigationGroups: action.navigationGroups
    })
  ),
  on(
    CommonActions.updateVehicleIdForUpcomingConfiguration,
    ConfigurationActions.acceptChangesAction,
    (state, action): MbccState => ({
      ...state,
      vehicleIdForUpcomingConfiguration: action.vehicleId
    })
  ),
  on(
    CommonActions.updateHeaderInfoHeight,
    (state, action): MbccState => ({
      ...state,
      headerInfoHeight: action.height
    })
  ),
  on(
    CommonActions.updateStage,
    (state, action): MbccState => ({
      ...state,
      stage: action.stage
        ? {
            ...action.stage,
            enforceReset: action.enforceReset
          }
        : state.stage
    })
  ),
  on(
    CommonActions.updateStageViewConfiguration,
    (state, action): MbccState => ({
      ...state,
      stageViewConfiguration: action.stageViewConfiguration
    })
  ),
  on(
    CommonActions.toggleLoadingSpinner,
    (state, action): MbccState => ({
      ...state,
      isLoading: action.isLoading
    })
  ),
  on(
    CommonActions.updateIsRowLayoutUsed,
    (state, action): MbccState => ({
      ...state,
      isRowLayoutUsed: action.isRowLayoutUsed
    })
  ),
  on(
    CommonActions.updateFilterResults,
    (state, action): MbccState => ({
      ...state,
      filterResult: action.filterResults
    })
  ),
  on(
    CommonActions.updateOpenModal,
    (state): MbccState => ({
      ...state,
      isModalOpen: true
    })
  ),
  on(
    CommonActions.updateCloseModal,
    (state): MbccState => ({
      ...state,
      isModalOpen: false
    })
  ),
  on(
    CommonActions.updateVehicleWishlistUuid,
    (state, action): MbccState => ({
      ...state,
      wishlistUuid: action.wishlistUuid
    })
  ),
  on(
    CommonActions.updateOnlineCodeData,
    (state, action): MbccState => ({
      ...state,
      onlineCodeData: {
        onlineCode: action.onlineCode,
        expirationDate: action.expirationDate,
        vcsId: action.vcsId,
        vehicleId: action.vehicleId,
        healed: action.healed
      } as OnlineCodeData
    })
  ),
  on(
    CommonActions.invalidateOnlineCode,
    (state): MbccState => ({
      ...state,
      onlineCodeData: { ...state.onlineCodeData, onlineCode: undefined } as OnlineCodeData
    })
  ),
  on(
    CommonActions.updateHasUserEnteredViaOnlineCode,
    (state, action): MbccState => ({
      ...state,
      onlineCodeData: { ...state.onlineCodeData, hasUserEnteredViaOnlineCode: action.hasUserEnteredViaOnlineCode },
      isFcisRateConfirmed: true
    })
  ),
  on(
    CommonActions.updateAuthState,
    (state, action): MbccState => ({
      ...state,
      authState: action.authState
    })
  ),
  on(
    CommonActions.updateVehicleImageUrl,
    (state, action): MbccState => ({
      ...state,
      vehicleImageUrl: action.vehicleImageUrl
    })
  ),
  on(
    CommonActions.updateBackendVersion,
    (state, action): MbccState => ({
      ...state,
      backendDataVersion: action.backendDataVersion
    })
  ),
  on(
    CommonActions.setIsInAemPreviewMode,
    (state, action): MbccState => ({ ...state, isInAemPreviewMode: action.isInAemPreviewMode })
  ),
  on(
    CommonActions.udpateBaseContext,
    (state, action): MbccState => ({
      ...state,
      executionContext: action.executionMode,
      onewebLanguage: action.onewebLanguage,
      onewebCountry: action.onewebCountry
    })
  ),
  on(
    CommonActions.updateB2bPriceVisibilityOnSummaryPage,
    (state, action): MbccState => ({
      ...state,
      isB2bPriceOnSummaryPageVisible: action.isB2bPriceOnSummaryPageVisible
    })
  ),
  on(
    CommonActions.updateIsPlsInitialized,
    (state, action): MbccState => ({
      ...state,
      isPlsInitialized: action.isPlsInitialized
    })
  ),
  on(
    CommonActions.stopAllVideos,
    (state, action): MbccState => ({
      ...state,
      // Note: if video id is not defined we just update with a random number (as string) to trigger a store update.
      lastIgnoredVideoIdWhenAllVideosStopped: action.lastIgnoredVideoIdWhenAllVideosStopped || String(secureRandom())
    })
  ),
  on(
    CommonActions.updateConfig,
    (state, action): MbccState => ({
      ...state,
      carlConfig: action.config
    })
  ),
  on(
    CommonActions.updateTranslations,
    (state, action): MbccState => ({
      ...state,
      carlTranslations: action.translations
    })
  ),
  on(
    CommonActions.updateVehicleSummary,
    (state, action): MbccState => ({ ...state, vehicleSummaryResult: action.result })
  ),
  on(
    CommonActions.invalidateVehicleSummaryDueToPostalCodeUpdate,
    (state): MbccState => ({
      ...state,
      vehicleSummaryResult: undefined
    })
  ),

  /**
   *
   * Financing
   *
   */
  on(
    FinancingActions.financingUpdateIsFcisInitialized,
    (state): MbccState => ({
      ...state,
      // always true intentionally
      isFcisInitialized: true
    })
  ),
  on(
    FinancingActions.financingUpdateIsFcisWidgetOpen,
    (state, action): MbccState => ({
      ...state,
      isFcisWidgetOpen: action.isFcisWidgetOpen
    })
  ),
  on(
    FinancingActions.financingUpdateIsFcisRateConfirmed,
    (state, action): MbccState => ({
      ...state,
      isFcisRateConfirmed: action.isFcisRateConfirmed,
      isFcisWidgetOpen: false // we also close the widget when rate got confirmed - intentionally
    })
  ),
  on(
    FinancingActions.financingUpdateFcisCalculationResult,
    (state, action): MbccState => ({
      ...state,
      calculationResult: action.calculationResult,
      // set availability of Fcis rate to true, as a result inidicates that there is no longer an api error
      isFcisCalculationAvailable: true
    })
  ),
  on(
    FinancingActions.financingUpdateIsFcisResultAvailable,
    (state, action): MbccState => ({
      ...state,
      isFcisCalculationAvailable: action.isFcisResultAvailable
    })
  ),
  on(
    FinancingActions.financingFetchFcisVersion,
    (state, action): MbccState => ({
      ...state,
      fcisVersion: action.fcisVersion
    })
  ),
  on(
    UserConfigActions.userConfigUpdatePostalCode,
    (state, action): MbccState => ({
      ...state,
      postalCode: action.postalCode
    })
  ),
  on(
    UserConfigActions.userConfigRemovePostalCode,
    (state): MbccState => ({
      ...state,
      postalCode: undefined
    })
  ),
  on(
    DealerLocatorActions.dealerLocatorFinishLoadAction,
    (state, action): MbccState => ({
      ...state,
      outlets: [...(state.outlets || []), action.payload.outlet]
    })
  ),

  /**
   *
   * Motorization
   *
   */
  on(
    MotorizationActions.finishUpdateVehiclesAction,
    (state, action): MbccState => ({
      ...state,
      vehicles: action.motorization.vehicles,
      vehicleLengths: action.motorization.lengths,
      preConfigurations: action.motorization.preConfigurations
    })
  ),
  on(
    MotorizationActions.clearMotorizationsAndFilterDataAction,
    (state): MbccState => ({ ...state, vehicles: undefined, filterData: undefined })
  ),
  on(
    MotorizationActions.updateFilterData,
    (state, action): MbccState => ({
      ...state,
      filterData: {
        filters: action.filters,
        sortingKeys: action.sortingKeys
      }
    })
  ),

  /**
   *
   * App-Error
   *
   */
  on(AppErrorActions.appErrorMajor, (state, action): MbccState => ({ ...state, currentError: action.error })),
  on(
    AppErrorActions.appErrorMinor,
    (state, action): MbccState => ({ ...state, currentError: action.errorWithNotification })
  ),

  /**
   *
   * Context
   *
   */
  on(
    RuntimeVariableActions.runtimeVariablesUpdate,
    (state, action): MbccState => ({
      ...state,
      ...Object.values(RuntimeVariables).reduce<MbccState>(
        (data, prop) => ({
          ...data,
          ...(Object.prototype.hasOwnProperty.call(action.payload, prop)
            ? { [prop]: action.payload[prop] as string }
            : {})
        }),
        {} as MbccState
      )
    })
  ),

  /**
   *
   * Configuration
   *
   */
  on(
    ConfigurationActions.finishInitializeAction,
    (state): MbccState => ({
      ...state,
      selectableCategories: undefined,
      selectableChangesQueue: undefined,
      selectableChangeInProcess: undefined,
      selectableChangeSource: undefined,
      isLoading: undefined,
      selectedCategories: undefined,
      isFilteredByCategory: undefined,
      isSimilarStockCarsAvailable: undefined
    })
  ),
  on(
    ConfigurationActions.finishLoadVehicleAction,
    (state, action): MbccState => ({
      ...state,
      vehicle: action.vehicle
    })
  ),
  on(
    ConfigurationActions.finishUpdateFeasibleVehicleAction,
    (state, action): MbccState => ({
      ...state,
      vehicle: action.vehicle
    })
  ),
  on(
    ConfigurationActions.finishUpdateVehicleAction,
    (state, action): MbccState => ({
      ...state,
      vehicle: action.vehicle
    })
  ),
  on(
    ConfigurationActions.finishLoadSelectablesAction,
    (state, action): MbccState => ({
      ...state,
      selectableCategories: action.componentCategories,
      selectableComponents: action.components
    })
  ),
  on(
    ConfigurationActions.finishLoadComponentInfoForCurrentVehicleAction,
    (state, action): MbccState => ({
      ...state,
      componentInfo: action.componentInfo
    })
  ),
  on(
    ConfigurationActions.finishEnqueueSelectableChangeAction,
    (state, action): MbccState => ({
      ...state,
      selectableChangesQueue: [...(state.selectableChangesQueue || []), action.selectableChange]
    })
  ),
  on(
    ConfigurationActions.startProcessSelectableChangeAction,
    (state, action): MbccState => ({
      ...state,
      // eslint-disable-next-line unicorn/no-useless-spread
      selectableChangesQueue: [...(state.selectableChangesQueue || []).slice(1)],
      selectableChangeInProcess: action.selectableChange,
      isLoading: true
    })
  ),
  on(
    ConfigurationActions.startEnqueueSelectableChangeAction,
    (state, action): MbccState => ({
      ...state,
      selectableChangeSource: action.source
    })
  ),
  on(
    ConfigurationActions.finishProcessSelectableChangeAction,
    (state): MbccState => ({
      ...state,
      selectableChangeInProcess: undefined,
      isLoading: false,
      selectableChangeSource: undefined
    })
  ),
  on(
    ConfigurationActions.startResolveFeasibilityConflictAction,
    (state): MbccState => ({
      ...state,
      selectableChangesQueue: [],
      isLoading: false
    })
  ),
  on(
    ConfigurationActions.finishResolveFeasibilityConflictAction,
    (state): MbccState => ({
      ...state,
      isLoading: true
    })
  ),
  on(
    ConfigurationActions.abortFeasibilityConflictAction,
    (state): MbccState => ({
      ...state,
      isLoading: true
    })
  ),
  on(
    ConfigurationActions.updateSelectedCategories,
    (state, action): MbccState => ({
      ...state,
      selectedCategories: action.selectedCategories
    })
  ),
  on(
    ConfigurationActions.updateIsFilteredByCategory,
    (state, action): MbccState => ({
      ...state,
      isFilteredByCategory: action.isFilteredByCategory
    })
  ),
  on(
    ConfigurationActions.updateIsSimilarStockCarsAvailable,
    (state, action): MbccState => ({
      ...state,
      isSimilarStockCarsAvailable: action.isSimilarStockCarsAvailable
    })
  ),
  on(
    CommonActions.udpateFeasibilityRequestPending,
    (state, action): MbccState => ({
      ...state,
      feasibilityRequestPending: action.feasibilityRequestPending
    })
  ),
  on(
    CommonActions.updateScreenMirroringCredentials,
    (state, action): MbccState => ({
      ...state,
      screenMirroringCredentials: action.credentials
    })
  ),
  on(
    CommonActions.updateInteriorParameter,
    (state, action): MbccState => ({
      ...state,
      interiorParameter: action.interiorParameter
    })
  ),
  on(
    CommonActions.updateInterior360DataUrl,
    (state, action): MbccState => ({
      ...state,
      interior360DataUrl: action.interior360DataUrl
    })
  ),
  on(
    CommonActions.updateStageView,
    (state, action): MbccState => ({
      ...state,
      stageView: action.stageView
    })
  ),
  on(
    CommonActions.updateIsSummarySidebarShown,
    (state, action): MbccState => ({
      ...state,
      isSummarySidebarShown: action.isSummarySidebarShown
    })
  ),
  on(
    ConfigurationActions.updateIsInitialMotorizationAction,
    (state, action): MbccState => ({
      ...state,
      isInitialMotorization: action.isInitialMotorization
    })
  ),
  on(
    CommonActions.updateDisplayShadowUnderStage,
    (state, action): MbccState => ({
      ...state,
      displayShadowUnderStage: action.displayShadowUnderStage
    })
  ),
  on(
    CommonActions.updateIsNextStepButtonVisible,
    (state, action): MbccState => ({
      ...state,
      isNextStepButtonVisible: action.isNextStepButtonVisible
    })
  ),
  on(
    CommonActions.storeIsInitiallyBaumusterEntryAction,
    (state, action): MbccState => ({
      ...state,
      isInitiallyBaumusterEntry: action.isInitiallyBaumusterEntry
    })
  ),
  on(
    CommonActions.setSelectedPreconfig,
    (state, action): MbccState => ({
      ...state,
      selectedPreconfig: action.selectedPreconfig
    })
  )
);
